// src/admin/pages/Invoices.jsx
import React, { useState, useMemo, useEffect } from "react";
import { FaSearch } from "react-icons/fa";
import {
  FunnelIcon,
  TableCellsIcon,
  ArrowDownOnSquareStackIcon,
} from "@heroicons/react/24/outline";
import { NavLink } from "react-router-dom";
import { obtenerFacturas, actualizarEstadoFactura } from "../../api";

const Invoices = () => {
  const [facturas, setFacturas] = useState([]);
  const [search, setSearch] = useState("");
  const [since, setSince] = useState("");
  const [before, setBefore] = useState("");
  const [customer, setCustomer] = useState("");
  const [order, setOrder] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(null);

  useEffect(() => {
    obtenerFacturas().then(setFacturas).catch(console.error);
  }, []);

  const handleStatusChange = async (invoiceId, newStatus) => {
  if (updatingStatus === invoiceId) return; // Prevent multiple changes
    
    setUpdatingStatus(invoiceId);
    try {
      await actualizarEstadoFactura(invoiceId, newStatus);
  // Update local state
      setFacturas(prev => 
        prev.map(inv => 
          inv.id === invoiceId 
            ? { ...inv, status: newStatus }
            : inv
        )
      );
    } catch (error) {
      console.error('Error updating invoice status:', error);
  // Here you could show a toast or error message
    } finally {
      setUpdatingStatus(null);
    }
  };

  const rows = useMemo(() => {
    return facturas.filter((inv) => {
      if (
        search &&
        !inv.customer.toLowerCase().includes(search.toLowerCase()) &&
        !inv.order_code.toLowerCase().includes(search.toLowerCase())
      )
        return false;
      if (
        since &&
        new Date(inv.date.split("/").reverse().join("-")) < new Date(since)
      )
        return false;
      if (
        before &&
        new Date(inv.date.split("/").reverse().join("-")) > new Date(before)
      )
        return false;
      if (customer && inv.customer !== customer) return false;
      if (order && inv.order_code !== order) return false;
      return true;
    });
  }, [search, since, before, customer, order, facturas]);

  return (
    <div className="flex-1 p-6 bg-gray-800 text-gray-200 h-full overflow-auto">
      <div className="flex flex-wrap gap-4 mb-4">
        <input
          type="date"
          value={since}
          onChange={(e) => setSince(e.target.value)}
          className="py-2 px-3 bg-gray-900 rounded placeholder-gray-500 text-gray-200"
          placeholder="From date"
        />
        <input
          type="date"
          value={before}
          onChange={(e) => setBefore(e.target.value)}
          className="py-2 px-3 bg-gray-900 rounded placeholder-gray-500 text-gray-200"
          placeholder="To date"
        />
        <select
          value={customer}
          onChange={(e) => setCustomer(e.target.value)}
          className="py-2 px-3 bg-gray-900 rounded"
        >
          <option value="">Customer</option>
          {[...new Set(facturas.map((i) => i.customer))].map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={order}
          onChange={(e) => setOrder(e.target.value)}
          className="py-2 px-3 bg-gray-900 rounded"
        >
          <option value="">Order</option>
          {[...new Set(facturas.map((i) => i.order_code))].map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="relative w-64">
          <FaSearch className="absolute left-3 top-2 text-gray-500" />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-3 py-2 w-full bg-gray-900 rounded placeholder-gray-500 text-gray-200"
          />
        </div>
        <div className="flex items-center space-x-6 text-sm uppercase">
          <button className="flex items-center space-x-1 hover:text-white">
            <FunnelIcon className="h-5 w-5" />
            <span>Add Filter</span>
          </button>
          <button className="flex items-center space-x-1 hover:text-white">
            <TableCellsIcon className="h-5 w-5" />
            <span>Columns</span>
          </button>
          <button className="flex items-center space-x-1 hover:text-white">
            <ArrowDownOnSquareStackIcon className="h-5 w-5" />
            <span>Export</span>
          </button>
        </div>
      </div>

      <table className="w-full table-auto">
        <thead className="bg-gray-700 text-gray-400 text-sm uppercase">
          <tr>
            <th className="px-4 py-2"></th>
            <th className="px-4 py-2 text-right">Id</th>
            <th className="px-4 py-2 text-left">Invoice date</th>
            <th className="px-4 py-2 text-left">Customer</th>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-left">Passed</th>
            <th className="px-4 py-2 text-left">Order</th>
            <th className="px-4 py-2 text-right">Subtotal</th>
            <th className="px-4 py-2 text-right">Taxes</th>
            <th className="px-4 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((inv) => (
            <tr
              key={inv.id}
              className="border-b border-gray-700 hover:bg-gray-700"
            >
              <td className="px-4 py-2">
                <button className="text-gray-400">&gt;</button>
              </td>
              <td className="px-4 py-2 text-right">{inv.id}</td>
              <td className="px-4 py-2">{inv.date}</td>
              <td className="px-4 py-2">
                <NavLink
                  to={`/admin/users/${inv.user_id}`}
                  className="text-primary hover:underline"
                >
                  {inv.customer}
                </NavLink>
              </td>
              <td className="px-4 py-2">
                <select
                  value={inv.status || 'draft'}
                  onChange={(e) => handleStatusChange(inv.id, e.target.value)}
                  disabled={updatingStatus === inv.id}
                  className={`rounded px-2 py-1 text-sm border focus:border-primary focus:outline-none ${
                    updatingStatus === inv.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  } ${
                    inv.status === 'paid' ? 'bg-green-700 text-green-100 border-green-600' :
                    inv.status === 'sent' ? 'bg-blue-700 text-blue-100 border-blue-600' :
                    inv.status === 'overdue' ? 'bg-red-700 text-red-100 border-red-600' :
                    'bg-gray-700 text-gray-200 border-gray-600'
                  }`}
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                </select>
              </td>
              <td className="px-4 py-2">{inv.passed}</td>
              <td className="px-4 py-2">
                <NavLink
                  to={`/admin/bookings/${inv.booking_id}`}
                  className="text-primary hover:underline"
                >
                  {inv.order_code}
                </NavLink>
              </td>
              <td className="px-4 py-2 text-right">
                {Number(inv.exTaxes).toFixed(2)}
              </td>
              <td className="px-4 py-2 text-right">
                {Number(inv.taxes).toFixed(2)}
              </td>
              <td className="px-4 py-2 text-right">
                {Number(inv.total).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Invoices;
